import { useState } from "react";
import { Sparkles, Upload, X, Download, Wand2, Video, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const toBase64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

export const AIStudioTab = () => {
  const [modelImg, setModelImg] = useState(null);
  const [assets, setAssets] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [quality, setQuality] = useState("flash");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const pickModel = async (e) => {
    const f = e.target.files?.[0];
    if (f) setModelImg(await toBase64(f));
  };
  const pickAssets = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6);
    const b64 = await Promise.all(files.map(toBase64));
    setAssets((prev) => [...prev, ...b64].slice(0, 6));
  };

  const generate = async (isEdit = false) => {
    if (!prompt.trim() && !isEdit) { toast.error("Describe the image you want."); return; }
    setLoading(true);
    try {
      const r = await api.post("/admin/ai/generate-image", {
        prompt,
        model_image: modelImg,
        asset_images: assets,
        previous_image: isEdit ? result : null,
        model_choice: quality,
      }, { timeout: 180000 });
      if (result) setHistory((h) => [result, ...h].slice(0, 8));
      setResult(r.data.image);
      toast.success(isEdit ? "Edited!" : "Image generated!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed.");
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!result) return;
    const a = document.createElement("a");
    a.href = result;
    a.download = `lira-ai-${Date.now()}.png`;
    a.click();
  };

  const [vidModel, setVidModel] = useState(null);
  const [vidPrompt, setVidPrompt] = useState("");
  const [vidDuration, setVidDuration] = useState("5");
  const [vidUrl, setVidUrl] = useState(null);
  const [vidLoading, setVidLoading] = useState(false);

  const pickVidModel = async (e) => { const f = e.target.files?.[0]; if (f) setVidModel(await toBase64(f)); };

  const genVideo = async () => {
    if (!vidModel) { toast.error("Upload the model image for the video."); return; }
    setVidLoading(true); setVidUrl(null);
    try {
      const r = await api.post("/admin/ai/generate-video", { model_image: vidModel, prompt: vidPrompt, duration: vidDuration });
      const jobId = r.data.job_id;
      const started = Date.now();
      let transientErrors = 0;
      const poll = setInterval(async () => {
        // give up after 10 minutes
        if (Date.now() - started > 600000) {
          clearInterval(poll); setVidLoading(false);
          toast.error("Video is taking too long. Please try again.");
          return;
        }
        try {
          const s = await api.get(`/admin/ai/job/${jobId}`);
          transientErrors = 0;
          if (s.data.status === "done") {
            clearInterval(poll);
            if (s.data.video_url) { setVidUrl(s.data.video_url); toast.success("Video ready!"); }
            else { toast.error("Video finished but no file was returned."); }
            setVidLoading(false);
          } else if (s.data.status === "error") {
            clearInterval(poll); setVidLoading(false);
            toast.error(s.data.error || "Video generation failed.");
          }
        } catch (err) {
          // tolerate a few transient network errors before giving up
          transientErrors += 1;
          if (transientErrors >= 4) {
            clearInterval(poll); setVidLoading(false);
            toast.error("Lost connection while checking video status.");
          }
        }
      }, 5000);
    } catch (e) { setVidLoading(false); toast.error(e?.response?.data?.detail || "Could not start video."); }
  };

  const inputCls = "w-full border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-black";

  return (
    <div className="space-y-8" data-testid="ai-studio-tab">
      {/* PHOTO STUDIO */}
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8">
        <div className="space-y-5">
          <div className="flex items-center gap-2">
            <ImageIcon strokeWidth={1.5} className="h-5 w-5 text-[#560216]" />
            <h3 className="eyebrow">AI Photo Studio</h3>
          </div>

          <div>
            <label className="eyebrow block mb-2">Model / person reference</label>
            {modelImg ? (
              <div className="relative inline-block">
                <img src={modelImg} alt="model" className="h-28 w-28 object-cover rounded-md border border-[#E5E5E5]" />
                <button onClick={() => setModelImg(null)} className="absolute -top-2 -right-2 bg-white rounded-full border p-0.5" data-testid="remove-model-img"><X className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <label className="flex items-center gap-2 border border-dashed border-[#d8d1c4] rounded-md px-4 py-4 cursor-pointer text-sm text-[#737373] hover:border-[#560216]" data-testid="upload-model-img">
                <Upload className="h-4 w-4" /> Upload the person to replicate
                <input type="file" accept="image/*" onChange={pickModel} className="hidden" />
              </label>
            )}
          </div>

          <div>
            <label className="eyebrow block mb-2">Additional assets <span className="text-[#a8a29a] normal-case">optional</span></label>
            <div className="flex flex-wrap gap-2 mb-2">
              {assets.map((a, i) => (
                <div key={i} className="relative">
                  <img src={a} alt="" className="h-16 w-16 object-cover rounded-md border border-[#E5E5E5]" />
                  <button onClick={() => setAssets(assets.filter((_, x) => x !== i))} className="absolute -top-2 -right-2 bg-white rounded-full border p-0.5"><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
            <label className="flex items-center gap-2 border border-dashed border-[#d8d1c4] rounded-md px-4 py-3 cursor-pointer text-sm text-[#737373] hover:border-[#560216]" data-testid="upload-assets">
              <Upload className="h-4 w-4" /> Add products / backgrounds
              <input type="file" accept="image/*" multiple onChange={pickAssets} className="hidden" />
            </label>
          </div>

          <div>
            <label className="eyebrow block mb-2">Describe the image</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} className={inputCls}
              placeholder="e.g. The model wearing the rose modal hijab, soft studio lighting, elegant beige backdrop, editorial fashion look…"
              data-testid="ai-prompt" />
          </div>

          <div>
            <label className="eyebrow block mb-1">Quality</label>
            <select value={quality} onChange={(e) => setQuality(e.target.value)} className={`${inputCls} bg-white`} data-testid="ai-quality">
              <option value="flash">Fast (Nano Banana)</option>
              <option value="pro">High detail (slower)</option>
            </select>
          </div>

          <button onClick={() => generate(false)} disabled={loading} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="ai-generate">
            <Sparkles className="h-4 w-4" /> {loading ? "Generating…" : "Generate Image"}
          </button>
        </div>

        {/* Result */}
        <div>
          <div className="border border-[#E5E5E5] rounded-md min-h-[360px] flex items-center justify-center bg-[#faf8f5] p-4">
            {loading ? (
              <p className="text-sm text-[#737373]">Creating your image… this can take up to a minute.</p>
            ) : result ? (
              <img src={result} alt="result" className="max-h-[520px] w-auto rounded" data-testid="ai-result-image" />
            ) : (
              <p className="text-sm text-[#737373]">Your generated image will appear here.</p>
            )}
          </div>
          {result && !loading && (
            <div className="mt-4 space-y-3">
              <div className="flex gap-2">
                <input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Ask the AI to edit this image…" className={inputCls} data-testid="ai-edit-prompt" />
                <button onClick={() => generate(true)} className="btn-secondary inline-flex items-center gap-1.5 shrink-0" data-testid="ai-edit"><Wand2 className="h-4 w-4" /> Edit</button>
              </div>
              <button onClick={download} className="btn-primary inline-flex items-center gap-2" data-testid="ai-download"><Download className="h-4 w-4" /> Download</button>
            </div>
          )}
          {history.length > 0 && (
            <div className="mt-5">
              <p className="eyebrow mb-2">Previous versions</p>
              <div className="flex flex-wrap gap-2">
                {history.map((h, i) => (
                  <img key={i} src={h} alt="" onClick={() => setResult(h)} className="h-16 w-16 object-cover rounded border border-[#E5E5E5] cursor-pointer hover:opacity-80" />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* VIDEO STUDIO */}
      <div className="border-t border-[#E5E5E5] pt-8" data-testid="ai-video-studio">
        <div className="flex items-center gap-2 mb-1">
          <Video strokeWidth={1.5} className="h-5 w-5 text-[#560216]" />
          <h3 className="eyebrow">AI Video Studio</h3>
        </div>
        <p className="text-xs text-[#a8a29a] mb-5 max-w-2xl">Turn a model photo into a short, lifelike clip. The AI generates new natural motion from your image + prompt (5–10 seconds).</p>

        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8">
          <div className="space-y-5">
            <div>
              <label className="eyebrow block mb-2">Model / person image</label>
              {vidModel ? (
                <div className="relative inline-block">
                  <img src={vidModel} alt="video model" className="h-28 w-28 object-cover rounded-md border border-[#E5E5E5]" />
                  <button onClick={() => setVidModel(null)} className="absolute -top-2 -right-2 bg-white rounded-full border p-0.5" data-testid="remove-vid-model"><X className="h-3.5 w-3.5" /></button>
                </div>
              ) : (
                <label className="flex items-center gap-2 border border-dashed border-[#d8d1c4] rounded-md px-4 py-4 cursor-pointer text-sm text-[#737373] hover:border-[#560216]" data-testid="upload-vid-model">
                  <Upload className="h-4 w-4" /> Upload the model photo to animate
                  <input type="file" accept="image/*" onChange={pickVidModel} className="hidden" />
                </label>
              )}
            </div>

            <div>
              <label className="eyebrow block mb-2">Describe the motion</label>
              <textarea value={vidPrompt} onChange={(e) => setVidPrompt(e.target.value)} rows={4} className={inputCls}
                placeholder="e.g. The model gently turns her head and smiles, the hijab flows softly in a light breeze, elegant editorial motion…"
                data-testid="vid-prompt" />
            </div>

            <div>
              <label className="eyebrow block mb-1">Duration</label>
              <select value={vidDuration} onChange={(e) => setVidDuration(e.target.value)} className={`${inputCls} bg-white`} data-testid="vid-duration">
                <option value="5">5 seconds</option>
                <option value="10">10 seconds</option>
              </select>
            </div>

            <button onClick={genVideo} disabled={vidLoading} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="generate-video">
              <Video className="h-4 w-4" /> {vidLoading ? "Generating…" : "Generate Video"}
            </button>
          </div>

          {/* Video result */}
          <div>
            <div className="border border-[#E5E5E5] rounded-md min-h-[360px] flex items-center justify-center bg-[#faf8f5] p-4">
              {vidLoading ? (
                <div className="text-center">
                  <div className="mx-auto mb-3 h-8 w-8 rounded-full border-2 border-[#560216] border-t-transparent animate-spin" />
                  <p className="text-sm text-[#737373]">Rendering your video… this usually takes 1–3 minutes. You can keep working — it'll appear here when ready.</p>
                </div>
              ) : vidUrl ? (
                <video src={vidUrl} controls autoPlay loop className="max-h-[520px] w-auto rounded" data-testid="vid-result" />
              ) : (
                <p className="text-sm text-[#737373]">Your generated video will appear here.</p>
              )}
            </div>
            {vidUrl && !vidLoading && (
              <div className="mt-4">
                <a href={vidUrl} download={`lira-ai-video-${Date.now()}.mp4`} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2" data-testid="vid-download">
                  <Download className="h-4 w-4" /> Download Video
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIStudioTab;
